package com.snakesworld.game.domain

import com.snakesworld.game.engine.Direction
import kotlin.math.abs
import kotlin.math.hypot

/**
 * Maps a playfield flick / swipe delta to one cardinal [Direction].
 *
 * The engine still applies 180° reversal lockout — this helper only
 * classifies motion. Tiny accidental pans are ignored until a distance
 * (or a short, fast flick) threshold is met.
 */
object ArenaSwipe {
    /** Default commit distance in px (~36.dp at mdpi; callers should convert dp). */
    const val DEFAULT_MIN_DISTANCE_PX = 36f

    /** Minimum travel for a quick flick that never reaches [DEFAULT_MIN_DISTANCE_PX]. */
    const val DEFAULT_MIN_FLICK_DISTANCE_PX = 16f

    /** Minimum speed (px/s) to treat a short stroke as an intentional flick. */
    const val DEFAULT_MIN_FLICK_VELOCITY_PX_PER_S = 600f

    /**
     * Dominant-axis cardinal from a raw delta. Returns null when there is no motion.
     */
    fun directionFromDelta(dx: Float, dy: Float): Direction? {
        if (dx == 0f && dy == 0f) return null
        return if (abs(dx) >= abs(dy)) {
            if (dx > 0f) Direction.RIGHT else Direction.LEFT
        } else {
            if (dy > 0f) Direction.DOWN else Direction.UP
        }
    }

    /**
     * Direction once the swipe has clearly committed to an axis, or null
     * if the motion is still below [minDistancePx].
     */
    fun committedDirection(
        dx: Float,
        dy: Float,
        minDistancePx: Float = DEFAULT_MIN_DISTANCE_PX,
    ): Direction? {
        if (hypot(dx, dy) < minDistancePx) return null
        return directionFromDelta(dx, dy)
    }

    /**
     * Fast flick that did not travel the full commit distance.
     */
    fun flickDirection(
        dx: Float,
        dy: Float,
        elapsedSeconds: Float,
        minDistancePx: Float = DEFAULT_MIN_FLICK_DISTANCE_PX,
        minVelocityPxPerSec: Float = DEFAULT_MIN_FLICK_VELOCITY_PX_PER_S,
    ): Direction? {
        val distance = hypot(dx, dy)
        if (distance < minDistancePx || elapsedSeconds <= 0f) return null
        if (distance / elapsedSeconds < minVelocityPxPerSec) return null
        return directionFromDelta(dx, dy)
    }
}
